import {
  DocNode,
  DocumentType,
  PlaceholderToken,
  PreUploadState,
} from '@qtek/shared/models';

export interface DocumentsState {
  companyLogo: string;
  companyLogoCacheBuster: number;
  companyLogoDoc: DocNode[];
  personalAvatar: string;
  entityDocuments: DocNode[];
  dataRoomEntityDocuments: DocNode[];
  dataRoomEntityMeta: unknown | null;
  invoiceAttachmentDocument: DocNode[];
  estimateAttachmentDocument: DocNode[];
  wcoAttachmentDocument: DocNode[];
  creatorLogos: string[];
  meetingDocuments: DocNode[];
  personalDocuments: DocNode[];
  bookingDocuments: DocNode[];
  menuDocuments: DocNode[];
  extrasDocuments: DocNode[];
  bookingQr: DocNode[];
  preUploadDocuments: PreUploadState[];
  isDataRoomEntityDocumentsLoading: boolean;
  placeholderToken: Partial<PlaceholderToken> | null;
  corporateNda: DocNode | null;
  updateDocumentWSFailureCount: number;
  preUploadTokenFailureCount: number;
  placeholderTokenFailureCount: number;
}

export const documentsInitialState: DocumentsState = {
  companyLogo: '',
  companyLogoCacheBuster: 0,
  companyLogoDoc: [],
  personalAvatar: '',
  entityDocuments: [],
  dataRoomEntityDocuments: [],
  dataRoomEntityMeta: null,
  invoiceAttachmentDocument: [],
  estimateAttachmentDocument: [],
  wcoAttachmentDocument: [],
  creatorLogos: [],
  meetingDocuments: [],
  personalDocuments: [],
  bookingDocuments: [],
  menuDocuments: [],
  extrasDocuments: [],
  bookingQr: [],
  preUploadDocuments: [],
  isDataRoomEntityDocumentsLoading: false,
  placeholderToken: null,
  corporateNda: null,
  updateDocumentWSFailureCount: 0,
  preUploadTokenFailureCount: 0,
  placeholderTokenFailureCount: 0,
};

export function applyUploadDocumentsSuccess(
  state: DocumentsState,
  payload: DocNode[]
): Partial<DocumentsState> {
  if (!Array.isArray(payload) || !payload.length) {
    return {};
  }

  switch (payload[0].attr) {
    case DocumentType.INVOICE_ATTACHMENT_DOCUMENT:
      return {
        invoiceAttachmentDocument:
          state.invoiceAttachmentDocument.concat(payload),
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.ESTIMATE_ATTACHMENT_DOCUMENT:
      return {
        estimateAttachmentDocument:
          state.estimateAttachmentDocument.concat(payload),
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.WCO_ATTACHMENT_DOCUMENT:
      return {
        wcoAttachmentDocument: state.wcoAttachmentDocument.concat(payload),
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.COMPANY_LOGO:
      return {
        companyLogo: payload[0]._id ?? state.companyLogo,
        companyLogoCacheBuster: state.companyLogoCacheBuster + 1,
        companyLogoDoc: payload,
      };
    case DocumentType.PERSONAL_AVATAR:
      return {};
    case DocumentType.MEETING_ATTACHMENT_DOCUMENT:
      return {
        meetingDocuments: state.meetingDocuments.concat(payload),
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.PERSONAL_DOCUMENTS:
      return {
        personalDocuments: state.personalDocuments.concat(payload),
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.COMPANY_BOOKING_BACKGROUND:
      return {
        bookingDocuments: state.bookingDocuments.concat(payload),
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.FOOD_MENU_ATTACHMENT_DOCUMENT:
      return {
        menuDocuments: state.menuDocuments.concat(payload),
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.SERVICE_EXTRA_IMAGE:
      return {
        extrasDocuments: state.extrasDocuments.concat(payload),
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.SERVICE_IMAGE:
    case DocumentType.ITEM_DOCUMENT:
      return {
        entityDocuments: state.entityDocuments.concat(payload),
      };
    case DocumentType.CORPORATE_NDA:
      return {
        corporateNda: payload[0],
      };
    default:
      return {};
  }
}

export function applyGetEntityDocumentsSuccess(
  state: DocumentsState,
  payload: DocNode[]
): Partial<DocumentsState> {
  if (!Array.isArray(payload) || !payload.length || !payload[0]) {
    return {};
  }

  switch (payload[0].attr) {
    case DocumentType.COMPANY_LOGO:
      return {
        companyLogo: payload[0]._id ?? state.companyLogo,
        companyLogoDoc: payload,
      };
    case DocumentType.PERSONAL_AVATAR:
      return {
        personalAvatar: payload[0]._id,
        entityDocuments: payload,
      };
    case DocumentType.INVOICE_ATTACHMENT_DOCUMENT:
      return {
        invoiceAttachmentDocument: payload,
        entityDocuments: payload,
      };
    case DocumentType.ESTIMATE_ATTACHMENT_DOCUMENT:
      return {
        estimateAttachmentDocument: payload,
        entityDocuments: payload,
      };
    case DocumentType.WCO_ATTACHMENT_DOCUMENT:
      return {
        wcoAttachmentDocument: payload,
        entityDocuments: payload,
      };
    case DocumentType.MEETING_ATTACHMENT_DOCUMENT:
      return {
        meetingDocuments: payload,
        entityDocuments: payload,
      };
    case DocumentType.PERSONAL_DOCUMENTS:
      return {
        personalDocuments: payload,
        entityDocuments: payload,
      };
    case DocumentType.COMPANY_BOOKING_BACKGROUND:
      return {
        bookingDocuments: payload,
        entityDocuments: payload,
      };
    case DocumentType.COMPANY_BOOKING_QR:
      return {
        bookingQr: payload,
        entityDocuments: payload,
      };
    case DocumentType.FOOD_MENU_ATTACHMENT_DOCUMENT:
      return {
        menuDocuments: payload,
        entityDocuments: payload,
      };
    case DocumentType.SERVICE_EXTRA_IMAGE:
      return {
        extrasDocuments: payload,
        entityDocuments: payload,
      };
    default:
      return { entityDocuments: payload };
  }
}

export function applyDocumentUpsert(
  state: DocumentsState,
  doc: DocNode
): Partial<DocumentsState> {
  const idx = state.dataRoomEntityDocuments.findIndex(
    document => document._id === doc._id
  );
  if (idx >= 0) {
    return {
      dataRoomEntityDocuments: state.dataRoomEntityDocuments.map(document =>
        document._id === doc._id ? { ...doc } : document
      ),
    };
  }
  return { dataRoomEntityDocuments: state.dataRoomEntityDocuments.concat(doc) };
}

export function applyDeleteDocumentSuccess(
  state: DocumentsState,
  payload: { docId: string; attr: DocumentType }
): Partial<DocumentsState> {
  const entityDocuments = state.entityDocuments.filter(
    document => document._id !== payload.docId
  );

  switch (payload.attr) {
    case DocumentType.COMPANY_LOGO:
      return {
        companyLogo: '',
        companyLogoDoc: state.companyLogoDoc.filter(
          document => document._id !== payload.docId
        ),
      };
    case DocumentType.INVOICE_ATTACHMENT_DOCUMENT:
      return {
        invoiceAttachmentDocument: state.invoiceAttachmentDocument.filter(
          document => document._id !== payload.docId
        ),
        entityDocuments,
      };
    case DocumentType.ESTIMATE_ATTACHMENT_DOCUMENT:
      return {
        estimateAttachmentDocument: state.estimateAttachmentDocument.filter(
          document => document._id !== payload.docId
        ),
        entityDocuments,
      };
    case DocumentType.WCO_ATTACHMENT_DOCUMENT:
      return {
        wcoAttachmentDocument: state.wcoAttachmentDocument.filter(
          document => document._id !== payload.docId
        ),
        entityDocuments,
      };
    case DocumentType.PERSONAL_AVATAR:
      return {
        personalAvatar: '',
        entityDocuments,
      };
    case DocumentType.MEETING_ATTACHMENT_DOCUMENT:
      return {
        meetingDocuments: state.meetingDocuments.filter(
          document => document._id !== payload.docId
        ),
        entityDocuments,
      };
    case DocumentType.PERSONAL_DOCUMENTS:
      return {
        personalDocuments: state.personalDocuments.filter(
          document => document._id !== payload.docId
        ),
        entityDocuments,
      };
    case DocumentType.COMPANY_BOOKING_BACKGROUND:
      return {
        bookingDocuments: state.bookingDocuments.filter(
          document => document._id !== payload.docId
        ),
        entityDocuments,
      };
    case DocumentType.FOOD_MENU_ATTACHMENT_DOCUMENT:
      return {
        menuDocuments: state.menuDocuments.filter(
          document => document._id !== payload.docId
        ),
        entityDocuments,
      };
    case DocumentType.SERVICE_IMAGE:
      return { entityDocuments };
    case DocumentType.SERVICE_EXTRA_IMAGE:
      return {
        entityDocuments,
        extrasDocuments: state.extrasDocuments.filter(
          document => document._id !== payload.docId
        ),
      };
    case DocumentType.ITEM_DOCUMENT:
      return { entityDocuments };
    case DocumentType.CORPORATE_NDA:
      return { corporateNda: null };
    case DocumentType.COMPANY_BOOKING_QR:
      return {
        bookingQr: state.bookingQr.filter(
          document => document._id !== payload.docId
        ),
      };
    default:
      return {};
  }
}
